
import React, { useState } from 'react';
import { 
  ArrowLeft, 
  MessageSquare, 
  TrendingDown, 
  TrendingUp, 
  Share2, 
  Globe, 
  Search, 
  Activity, 
  BarChart3, 
  Zap,
  TrendingUp as TrendingUpIcon
} from 'lucide-react';

export const ReputationView: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const [query, setQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState<any>(null);

  const handleSearch = () => {
    if (!query) return;
    setIsSearching(true);
    setTimeout(() => {
      setResults({
        entity: query,
        score: 85,
        trend: '+4.2%',
        volume: '12.4k Mentions',
        sentiment: 'Bullish',
        breakdown: { pos: 85, neu: 10, neg: 5 },
        stream: [
          { source: 'Reuters', text: `${query} reports record quarterly revenue growth.`, time: '2m ago', impact: 'high' },
          { source: 'Reddit', text: `Consensus building on ${query} new product launch.`, time: '12m ago', impact: 'med' },
          { source: 'X/Twitter', text: `Market analysts upgrade target for ${query}.`, time: '1h ago', impact: 'high' }
        ]
      });
      setIsSearching(false);
    }, 1500);
  };

  return (
    <div className="p-6 space-y-8 animate-in slide-in-from-right duration-500 max-w-6xl mx-auto">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button onClick={onBack} className="p-2 hover:bg-slate-200 rounded-full transition-colors text-slate-600">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-black text-slate-900 tracking-tight">Market Sentiment</h1>
            <p className="text-sm text-slate-500 font-medium tracking-tight">Real-time social & news perception via ESPY.com API.</p>
          </div>
        </div>
        <span className="px-3 py-1 bg-blue-50 text-blue-600 border border-blue-100 rounded-full text-[10px] font-black uppercase tracking-widest">Sentiment-AI v2.4 Live</span>
      </div>

      <div className="bg-slate-900 p-8 rounded-[3rem] shadow-2xl relative overflow-hidden">
        <div className="relative z-10">
          <h2 className="text-white text-lg font-black mb-6 flex items-center">
            <Search size={18} className="mr-2 text-blue-400" /> Reputation Engine
          </h2>
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <Activity className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
              <input 
                type="text" 
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search Brand, Stock, or Person (e.g. Acme Financial)" 
                className="w-full bg-white/5 border border-white/10 rounded-2xl px-12 py-4 text-white outline-none focus:ring-4 focus:ring-blue-500/20 transition-all font-medium"
              />
            </div>
            <button 
              onClick={handleSearch}
              disabled={isSearching}
              className="bg-blue-600 text-white px-8 py-4 rounded-2xl font-black uppercase text-xs tracking-widest hover:bg-blue-700 transition-all active:scale-95 disabled:opacity-50"
            >
              {isSearching ? 'Analyzing Stream...' : 'Analyze Reputation'}
            </button>
          </div>
        </div>
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600 rounded-full blur-[100px] opacity-10 translate-x-1/2 -translate-y-1/2"></div>
      </div>

      {results && !isSearching ? (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 animate-in zoom-in-95 duration-500">
          <div className="lg:col-span-4 space-y-6">
            <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm flex flex-col items-center justify-center text-center">
               <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6">Confidence Gauge</h3>
               <div className="relative w-48 h-24 overflow-hidden">
                  <div className="absolute inset-0 border-[16px] border-slate-100 rounded-t-full"></div>
                  <div className="absolute inset-0 border-[16px] border-blue-500 rounded-t-full" style={{ clipPath: `inset(0 ${100 - results.score}% 0 0)` }}></div>
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 text-center">
                     <p className="text-2xl font-black text-slate-900">{results.score}%</p>
                     <p className="text-[10px] font-bold text-blue-600 uppercase">{results.sentiment}</p>
                  </div>
               </div>
               <div className="mt-8 grid grid-cols-3 gap-6 w-full pt-6 border-t border-slate-50">
                  <StatItem val={`${results.breakdown.neg}%`} label="Neg" color="text-rose-500" />
                  <StatItem val={`${results.breakdown.neu}%`} label="Neu" color="text-slate-400" />
                  <StatItem val={`${results.breakdown.pos}%`} label="Pos" color="text-emerald-500" />
               </div>
            </div>

            <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm">
               <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">Volume Statistics</h4>
               <div className="space-y-4">
                  <DetailRow label="Total Buzz" value={results.volume} />
                  <DetailRow label="Momentum" value={results.trend} valueColor="text-emerald-600" />
                  <DetailRow label="Last Signal" value="2m ago" />
               </div>
            </div>
          </div>

          <div className="lg:col-span-8 bg-white p-10 rounded-[3rem] border border-slate-200 shadow-sm overflow-hidden">
             <div className="flex items-center justify-between mb-8">
                <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest flex items-center">
                  <MessageSquare size={18} className="mr-2 text-blue-600" /> Perception Stream
                </h3>
                <div className="flex space-x-2">
                   <button className="px-3 py-1 bg-slate-50 border border-slate-100 rounded-lg text-[9px] font-black uppercase tracking-widest text-slate-500">News Only</button>
                   <button className="px-3 py-1 bg-slate-50 border border-slate-100 rounded-lg text-[9px] font-black uppercase tracking-widest text-slate-500">Social Only</button>
                </div>
             </div>
             
             <div className="space-y-4">
                {results.stream.map((item: any, i: number) => (
                  <div key={i} className="flex items-start space-x-4 p-5 rounded-2xl bg-slate-50 border border-slate-100 group hover:bg-white hover:border-blue-200 transition-all">
                     <div className={`p-2 rounded-xl bg-white shadow-sm ${item.impact === 'high' ? 'text-blue-600' : 'text-slate-400'}`}>
                        <Zap size={18} className={item.impact === 'high' ? 'animate-pulse' : ''} />
                     </div>
                     <div className="flex-1">
                        <div className="flex justify-between mb-1.5">
                           <span className="text-[10px] font-black text-blue-600 uppercase tracking-widest">{item.source}</span>
                           <span className="text-[10px] text-slate-400 font-bold uppercase">{item.time}</span>
                        </div>
                        <p className="text-sm font-medium text-slate-800 leading-relaxed mb-3">{item.text}</p>
                        <div className="flex items-center space-x-4">
                           <button className="text-[9px] font-black text-slate-400 uppercase hover:text-blue-600 flex items-center">
                              <Share2 size={10} className="mr-1" /> Share Intelligence
                           </button>
                           <button className="text-[9px] font-black text-slate-400 uppercase hover:text-blue-600 flex items-center">
                              <Globe size={10} className="mr-1" /> View Source
                           </button>
                        </div>
                     </div>
                  </div>
                ))}
             </div>
          </div>
        </div>
      ) : (
        !isSearching && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 h-48 opacity-40">
             <PlaceholderBox icon={<BarChart3 size={32} />} title="Historical Trends" />
             <PlaceholderBox icon={<Globe size={32} />} title="Geo Distribution" />
             <PlaceholderBox icon={<TrendingUpIcon size={32} />} title="Risk Projections" />
          </div>
        )
      )}
    </div>
  );
};

const StatItem: React.FC<{ val: string, label: string, color: string }> = ({ val, label, color }) => (
  <div className="flex flex-col">
    <span className={`${color} font-black text-lg leading-none`}>{val}</span>
    <span className="text-[9px] text-slate-400 font-bold uppercase tracking-widest mt-1">{label}</span>
  </div>
);

const DetailRow: React.FC<{ label: string, value: string, valueColor?: string }> = ({ label, value, valueColor = 'text-slate-900' }) => (
  <div className="flex justify-between items-center text-[10px] font-black uppercase tracking-widest border-b border-slate-50 pb-2">
     <span className="text-slate-400">{label}</span>
     <span className={valueColor}>{value}</span>
  </div>
);

const PlaceholderBox: React.FC<{ icon: React.ReactNode, title: string }> = ({ icon, title }) => (
  <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm border-dashed flex flex-col items-center justify-center text-center">
     <div className="text-slate-200 mb-3">{icon}</div>
     <h4 className="text-[10px] font-black text-slate-300 uppercase tracking-widest">{title}</h4>
  </div>
);
